import { useState } from "react";

import {
  createCommunity,
  createGarret,
  createLeaf,
  deleteCommunity,
  deleteGarret,
  deleteLeaf,
  fetchUsersByOrg,
  moveUsersOrg,
  updateCommunity,
  updateGarret,
  updateLeaf,
} from "@/apis/admin";
import useModal from "@/hooks/useModal";
import {
  CAMPUS_CD,
  Community,
  Garret,
  Leaf,
  OpenUserListParams,
  OrgActionsState,
  OrgTreeState,
  OrgUser,
  PendingDelete,
  UserListOrg,
} from "@/pages/Admin/components/OrgManagement/types";

/* 조직 CRUD/이동/삭제 확인 등 액션 상태. OrgManagement 메인 컴포넌트에서만 호출된다. */
const useOrgActions = (tree: OrgTreeState): OrgActionsState => {
  const {
    selectedCmtCd,
    setSelectedCmtCd,
    selectedGarCd,
    setSelectedGarCd,
    selectedLeafCd,
    setSelectedLeafCd,
    loadCommunities,
    loadGarrets,
    loadLeafs,
  } = tree;

  // 인라인 추가 폼 표시
  const [showAddCmt, setShowAddCmt] = useState(false);
  const [showAddGar, setShowAddGar] = useState(false);
  const [showAddLeaf, setShowAddLeaf] = useState(false);

  // 인라인 편집
  const [editCmtCd, setEditCmtCd] = useState<string | null>(null);
  const [editCmtNm, setEditCmtNm] = useState("");
  const [editGarCd, setEditGarCd] = useState<string | null>(null);
  const [editGarNm, setEditGarNm] = useState("");
  const [editLeafCd, setEditLeafCd] = useState<string | null>(null);
  const [editLeafNm, setEditLeafNm] = useState("");

  const clearAllEdits = () => {
    setEditCmtCd(null);
    setEditGarCd(null);
    setEditLeafCd(null);
  };

  // 액션 로딩 상태 (중복 클릭 방지)
  const [actionLoading, setActionLoading] = useState(false);

  // 삭제 확인 모달
  const confirmModal = useModal();
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(
    null,
  );

  // 유저 목록 모달
  const userListModal = useModal();
  const [userListOrg, setUserListOrg] = useState<UserListOrg | null>(null);

  const openUserList = async (params: OpenUserListParams) => {
    try {
      const data = await fetchUsersByOrg({
        cpsCd: params.cpsCd,
        cmtCd: params.cmtCd,
        garCd: params.garCd,
        leafCd: params.leafCd,
      });
      const users: OrgUser[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.content)
          ? data.content
          : [];

      setUserListOrg({ name: params.name, users });
      userListModal.onOpen();
    } catch (e) {
      console.error(e);
    }
  };

  const handleMoveUsers = async (
    userIds: string[],
    cmtCd: string,
    garCd: string,
    leafCd: string,
  ) => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      await moveUsersOrg({ userIds, cmtCd, garCd, leafCd });
      userListModal.onClose();
      setUserListOrg(null);
      // 관련 데이터 새로고침
      if (selectedCmtCd) loadGarrets(selectedCmtCd);
      if (selectedGarCd && selectedCmtCd)
        loadLeafs(selectedCmtCd, selectedGarCd);
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  // ---- 공동체 CRUD ----

  const handleAddCommunity = async (values: Record<string, string>) => {
    if (!values.cmtNm?.trim() || actionLoading) return;
    setActionLoading(true);
    try {
      await createCommunity({
        cpsCd: CAMPUS_CD,
        cmtNm: values.cmtNm.trim(),
      });
      setShowAddCmt(false);
      await loadCommunities();
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveCommunity = async () => {
    if (!editCmtCd || !editCmtNm.trim() || actionLoading) return;
    setActionLoading(true);
    try {
      await updateCommunity({ cmtCd: editCmtCd, cmtNm: editCmtNm.trim() });
      setEditCmtCd(null);
      await loadCommunities();
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestDeleteCommunity = (c: Community) => {
    setPendingDelete({ type: "community", cd: c.cmtCd, nm: c.cmtNm });
    confirmModal.onOpen();
  };

  // ---- 다락방 CRUD ----

  const handleAddGarret = async (values: Record<string, string>) => {
    if (!selectedCmtCd || !values.garNm?.trim() || actionLoading) return;
    setActionLoading(true);
    try {
      await createGarret({
        cpsCd: CAMPUS_CD,
        cmtCd: selectedCmtCd,
        garNm: values.garNm.trim(),
      });
      setShowAddGar(false);
      await loadGarrets(selectedCmtCd);
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveGarret = async () => {
    if (!editGarCd || !editGarNm.trim() || actionLoading) return;
    setActionLoading(true);
    try {
      await updateGarret({ garCd: editGarCd, garNm: editGarNm.trim() });
      setEditGarCd(null);
      if (selectedCmtCd) await loadGarrets(selectedCmtCd);
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestDeleteGarret = (g: Garret) => {
    setPendingDelete({ type: "garret", cd: g.garCd, nm: g.garNm });
    confirmModal.onOpen();
  };

  // ---- 순 CRUD ----

  const handleAddLeaf = async (values: Record<string, string>) => {
    if (
      !selectedCmtCd ||
      !selectedGarCd ||
      !values.leafNm?.trim() ||
      actionLoading
    )
      return;
    setActionLoading(true);
    try {
      await createLeaf({
        cpsCd: CAMPUS_CD,
        cmtCd: selectedCmtCd,
        garCd: selectedGarCd,
        leafNm: values.leafNm.trim(),
      });
      setShowAddLeaf(false);
      await loadLeafs(selectedCmtCd, selectedGarCd);
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveLeaf = async () => {
    if (!editLeafCd || !editLeafNm.trim() || actionLoading) return;
    setActionLoading(true);
    try {
      await updateLeaf({ leafCd: editLeafCd, leafNm: editLeafNm.trim() });
      setEditLeafCd(null);
      if (selectedCmtCd && selectedGarCd)
        await loadLeafs(selectedCmtCd, selectedGarCd);
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestDeleteLeaf = (l: Leaf) => {
    setPendingDelete({ type: "leaf", cd: l.leafCd, nm: l.leafNm });
    confirmModal.onOpen();
  };

  // ---- 삭제 확인 ----

  const handleConfirmDelete = async () => {
    if (!pendingDelete || actionLoading) return;
    setActionLoading(true);
    try {
      if (pendingDelete.type === "community") {
        await deleteCommunity(pendingDelete.cd);
        if (selectedCmtCd === pendingDelete.cd) setSelectedCmtCd(null);
        await loadCommunities();
      } else if (pendingDelete.type === "garret") {
        await deleteGarret(pendingDelete.cd);
        if (selectedGarCd === pendingDelete.cd) setSelectedGarCd(null);
        if (selectedCmtCd) await loadGarrets(selectedCmtCd);
      } else {
        await deleteLeaf(pendingDelete.cd);
        if (selectedLeafCd === pendingDelete.cd) setSelectedLeafCd(null);
        if (selectedCmtCd && selectedGarCd)
          await loadLeafs(selectedCmtCd, selectedGarCd);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setActionLoading(false);
      confirmModal.onClose();
      setPendingDelete(null);
    }
  };

  return {
    showAddCmt,
    setShowAddCmt,
    showAddGar,
    setShowAddGar,
    showAddLeaf,
    setShowAddLeaf,
    editCmtCd,
    setEditCmtCd,
    editCmtNm,
    setEditCmtNm,
    editGarCd,
    setEditGarCd,
    editGarNm,
    setEditGarNm,
    editLeafCd,
    setEditLeafCd,
    editLeafNm,
    setEditLeafNm,
    clearAllEdits,
    confirmModal,
    pendingDelete,
    setPendingDelete,
    userListModal,
    userListOrg,
    setUserListOrg,
    openUserList,
    handleMoveUsers,
    handleAddCommunity,
    handleSaveCommunity,
    handleRequestDeleteCommunity,
    handleAddGarret,
    handleSaveGarret,
    handleRequestDeleteGarret,
    handleAddLeaf,
    handleSaveLeaf,
    handleRequestDeleteLeaf,
    handleConfirmDelete,
  };
};

export default useOrgActions;
